/**
 * Pure core for the optimistic alert resolve (KAN-2553 follow-up).
 *
 * Resolving an alert takes it out of every cached view that can only ever show
 * UNRESOLVED alerts — Home's Critical alerts, the Requires attention tab, the
 * participant's Requires attention tab and the Requires attention category
 * badges — before the server has answered. Views that can show resolved tasks
 * (the Tasks tab with no `resolved` filter, or `resolved=true`) are left alone:
 * the resolved row legitimately stays there.
 *
 * Everything here is a pure function over `[queryKey, data]` pairs; the
 * react-query reads/writes live in `hooks/useHideResolvedAlert.ts`.
 */

import { hashKey, QueryKey } from "@tanstack/react-query";
import { Task } from "../models/entities/task.model";

/** List of tasks, keyed `["participants-tasks", BackendPagination]` (useTasks). */
export const TASKS_LIST_KEY = "participants-tasks";

/**
 * Counts that follow a list with the same params: the table total
 * (useTasksCount) and the Requires attention category badges
 * (useTasksCountsByCategory, whose selected-category entry has exactly the
 * table's params).
 */
export const TASKS_COUNT_KEYS = ["participants-tasks-count", "participants-alerts-tasks-count"];

export const RESOLVED_ALERT_CACHE_KEYS = [TASKS_LIST_KEY, ...TASKS_COUNT_KEYS];

/** The RSQL comparison every unresolved-only view sends. */
const UNRESOLVED_COMPARISON = "resolved==false";

/**
 * Split an RSQL expression on one operator (`;` AND, `,` OR) at parenthesis
 * depth 0 only.
 */
const splitTopLevel = (rsql: string, separator: ";" | ","): string[] => {
	const parts: string[] = [];
	let depth = 0;
	let current = "";
	for (const char of rsql) {
		if (char === "(") depth += 1;
		if (char === ")") depth -= 1;
		if (char === separator && depth === 0) {
			parts.push(current);
			current = "";
		} else {
			current += char;
		}
	}
	parts.push(current);
	return parts.map((part) => part.trim());
};

/**
 * True only when the query's params restrict it to unresolved tasks, i.e. its
 * RSQL filter is an AND whose top level contains `resolved==false` on its own.
 *
 * `RsqlFormatter.filters` emits an AND of per-field ORs, so:
 * - Requires attention / Home / participant tab: `...;resolved==false;...` → true
 * - Tasks tab, no resolved filter: no `resolved` term → false
 * - Tasks tab, "Yes" + "No": `(resolved==true,resolved==false)` → false
 * - Tasks tab, "Yes" only: `resolved==true` → false
 * - Tasks tab, "No" only: `resolved==false` → true (a resolved row cannot be
 *   in that view, so removing it is correct there too)
 *
 * Anything it does not recognise (no params, no filters, a top-level OR) is
 * treated as "may show resolved rows" — the safe answer is to leave the row.
 */
export const isUnresolvedOnlyTaskQuery = (params: unknown): boolean => {
	if (!params || typeof params !== "object") return false;
	const filters = (params as { filters?: unknown }).filters;
	if (typeof filters !== "string" || filters.trim() === "") return false;
	if (splitTopLevel(filters, ",").length > 1) return false;
	return splitTopLevel(filters, ";").includes(UNRESOLVED_COMPARISON);
};

/**
 * One cached list and its count with one alert taken out: the count is lowered
 * by exactly the number of rows actually removed (never below zero, unchanged
 * if the id was not in the list). The inputs are not mutated; `undefined`
 * (nothing cached yet) passes through untouched.
 */
export const removeAlertFromCache = (
	alerts: Task[] | undefined,
	count: number | undefined,
	alertId: number
): { alerts: Task[] | undefined; count: number | undefined } => {
	if (!alerts) return { alerts, count };
	const remaining = alerts.filter((alert) => alert.id !== alertId);
	const removed = alerts.length - remaining.length;
	return {
		alerts: remaining,
		count: count === undefined ? count : Math.max(0, count - removed),
	};
};

export interface CacheEntry {
	queryKey: QueryKey;
	data: unknown;
}

/**
 * Which cache entries to rewrite, and to what, when `alertId` is resolved.
 *
 * - A list under `participants-tasks` is rewritten only if its params are
 *   unresolved-only AND it actually contains the alert.
 * - A count (table total or category badge) is lowered only if the list with
 *   the SAME params had the row. A count whose list is not cached, or whose
 *   list did not contain the row (another page, another category), is left for
 *   the server refetch — lowering it would be a guess.
 *
 * Entries that do not change are not returned.
 */
export const planResolvedAlertRemoval = (entries: CacheEntry[], alertId: number): CacheEntry[] => {
	const updates: CacheEntry[] = [];
	const removedByParams = new Map<string, number>();

	entries.forEach(({ queryKey, data }) => {
		if (queryKey[0] !== TASKS_LIST_KEY || !Array.isArray(data)) return;
		if (!isUnresolvedOnlyTaskQuery(queryKey[1])) return;
		const next = removeAlertFromCache(data as Task[], undefined, alertId);
		const removed = data.length - (next.alerts?.length ?? data.length);
		if (removed === 0) return;
		removedByParams.set(hashKey([queryKey[1]]), removed);
		updates.push({ queryKey, data: next.alerts });
	});

	entries.forEach(({ queryKey, data }) => {
		if (!TASKS_COUNT_KEYS.includes(queryKey[0] as string) || typeof data !== "number") return;
		if (!isUnresolvedOnlyTaskQuery(queryKey[1])) return;
		const removed = removedByParams.get(hashKey([queryKey[1]]));
		if (!removed) return;
		updates.push({ queryKey, data: Math.max(0, data - removed) });
	});

	return updates;
};
